import React, { useState, useEffect, ChangeEvent } from 'react';
import styled from 'styled-components';
import { AlertTriangle, Save, Plus, Pencil, Trash2 } from 'lucide-react';
import { Allergene } from '../types';
import apiService from '../services/api';
import GelatoDataGrid from '../components/DataGrid/GelatoDataGrid';
import { ColDef } from 'ag-grid-community';
import {
  PageContainer,
  PageHeader,
  Button,
  Card,
  CardBody,
  Alert,
  LoadingState,
  Modal,
  FormGroup,
  FormActions,
  Label,
  Input,
  Select,
  useConfirm,
} from '../components/ui';

const GridActionsCell = styled.div`
  display: flex;
  gap: var(--space-sm);
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const GridIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: var(--space-xs);
  border-radius: var(--border-radius-sm);
  color: var(--gray-600);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);

  svg {
    pointer-events: none;
  }

  &.btn-edit:hover {
    background: var(--primary-50);
    color: var(--primary-700);
  }

  &.btn-delete:hover {
    background: var(--error-soft);
    color: var(--error);
  }
`;

const ImpostazioniAllergeniPage: React.FC = () => {
  const confirm = useConfirm();
  const [allergeni, setAllergeni] = useState<Allergene[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAllergene, setEditingAllergene] = useState<Allergene | null>(null);
  const [alert, setAlert] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  });

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    codice: '',
    descrizione: '',
    regolamentazione: '',
    attivo: true
  });

  // Definizione colonne per AG Grid
  const columnDefs: ColDef[] = [
    {
      headerName: 'Nome',
      field: 'nome',
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 150
    },
    {
      headerName: 'Codice',
      field: 'codice',
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 120,
      valueGetter: params => params.data.codice || '-'
    },
    {
      headerName: 'Descrizione',
      field: 'descrizione',
      sortable: true,
      filter: true,
      flex: 2,
      minWidth: 200,
      valueGetter: params => params.data.descrizione || '-'
    },
    {
      headerName: 'Regolamentazione',
      field: 'regolamentazione',
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 150,
      valueGetter: params => params.data.regolamentazione || '-'
    },
    {
      headerName: 'Stato',
      field: 'attivo',
      sortable: true,
      filter: true,
      width: 100,
      cellRenderer: (params: any) => {
        const isAttivo = params.value;
        return React.createElement('span', {
          className: isAttivo ? 'status-badge active' : 'status-badge inactive'
        }, isAttivo ? 'Attivo' : 'Disattivo');
      }
    },
    {
      headerName: 'Azioni',
      field: 'actions',
      sortable: false,
      filter: false,
      width: 120,
      pinned: 'right',
      cellRenderer: (params: any) => (
        <GridActionsCell className="grid-actions-cell" data-row-id={params.data.id}>
          <GridIconButton
            className="btn-edit"
            data-action="edit"
            data-id={params.data.id}
            title="Modifica"
            aria-label="Modifica"
          >
            <Pencil size={16} />
          </GridIconButton>
          <GridIconButton
            className="btn-delete"
            data-action="delete"
            data-id={params.data.id}
            title="Elimina"
            aria-label="Elimina"
          >
            <Trash2 size={16} />
          </GridIconButton>
        </GridActionsCell>
      )
    }
  ];

  useEffect(() => {
    caricaAllergeni();
  }, []);

  const caricaAllergeni = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllergeni();
      setAllergeni(data);
    } catch (error) {
      console.error('Errore caricamento allergeni:', error);
      setAlert({ show: true, message: 'Errore nel caricamento degli allergeni', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingAllergene) {
        await apiService.aggiornaAllergene(editingAllergene.id, formData);
        setAlert({ show: true, message: 'Allergene aggiornato con successo', type: 'success' });
      } else {
        await apiService.creaAllergene(formData);
        setAlert({ show: true, message: 'Allergene creato con successo', type: 'success' });
      }

      setDialogOpen(false);
      caricaAllergeni();
    } catch (error) {
      console.error('Errore salvataggio allergene:', error);
      setAlert({ show: true, message: 'Errore nel salvataggio dell\'allergene', type: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: 'Elimina allergene',
      message: 'Sei sicuro di voler eliminare questo allergene?',
      confirmLabel: 'Elimina',
      variant: 'danger'
    });
    if (!ok) return;

    try {
      await apiService.eliminaAllergene(id);
      setAlert({ show: true, message: 'Allergene eliminato con successo', type: 'success' });
      caricaAllergeni();
    } catch (error) {
      console.error('Errore eliminazione allergene:', error);
      setAlert({ show: true, message: 'Errore nell\'eliminazione dell\'allergene', type: 'error' });
    }
  };

  // Gestione click azioni nella griglia AG Grid
  const onGridReady = (params: any) => {
    const gridElement = params.api.getGridOption('domLayout') === 'autoHeight'
      ? params.api.gridOptionsWrapper?.gridOptions?.context?.container
      : document.querySelector('.ag-root-wrapper');

    if (gridElement) {
      gridElement.addEventListener('click', (event: any) => {
        const target = event.target.closest('.btn-edit, .btn-delete');
        if (target) {
          const action = target.dataset.action;
          const id = parseInt(target.dataset.id);

          if (action === 'edit') {
            const allergene = allergeni.find(a => a.id === id);
            if (allergene) {
              setEditingAllergene(allergene);
              setFormData({
                nome: allergene.nome,
                codice: allergene.codice || '',
                descrizione: allergene.descrizione || '',
                regolamentazione: allergene.regolamentazione || '',
                attivo: allergene.attivo
              });
              setDialogOpen(true);
            }
          } else if (action === 'delete') {
            handleDelete(id);
          }
        }
      });
    }
  };

  const openAddDialog = () => {
    setEditingAllergene(null);
    setFormData({
      nome: '',
      codice: '',
      descrizione: '',
      regolamentazione: '',
      attivo: true
    });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Allergeni"
        icon={<AlertTriangle />}
        actions={
          <Button onClick={openAddDialog}>
            <Plus size={16} />
            Aggiungi Allergene
          </Button>
        }
      />

      {alert.show && (
        <Alert $variant={alert.type}>
          {alert.message}
        </Alert>
      )}

      <Card>
        <CardBody>
          <GelatoDataGrid
            rowData={allergeni}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            loading={loading}
          />
        </CardBody>
      </Card>

      <Modal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editingAllergene ? 'Modifica Allergene' : 'Nuovo Allergene'}
      >
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Nome</Label>
            <Input
              name="nome"
              value={formData.nome}
              onChange={handleInputChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Codice</Label>
            <Input
              name="codice"
              value={formData.codice}
              onChange={handleInputChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Descrizione</Label>
            <Input
              name="descrizione"
              value={formData.descrizione}
              onChange={handleInputChange}
            />
          </FormGroup>

          <FormGroup>
            <Label>Regolamentazione</Label>
            <Input
              name="regolamentazione"
              value={formData.regolamentazione}
              onChange={handleInputChange}
            />
          </FormGroup>

          <FormGroup>
            <Label>Stato</Label>
            <Select name="attivo" value={formData.attivo.toString()} onChange={handleInputChange}>
              <option value="true">Attivo</option>
              <option value="false">Disattivo</option>
            </Select>
          </FormGroup>

          <FormActions>
            <Button $variant="secondary" type="button" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button type="submit">
              <Save size={16} />
              Salva
            </Button>
          </FormActions>
        </form>
      </Modal>
    </PageContainer>
  );
};

export default ImpostazioniAllergeniPage;
